import React, { useState, useRef, useEffect } from 'react';
import { Mic, MicOff, Square, Play, Pause, Trash2, Send, RotateCcw, Volume2 } from 'lucide-react';

const VoiceRecorder = ({ onRecordingComplete, onCancel }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [mediaRecorder, setMediaRecorder] = useState(null);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [isHolding, setIsHolding] = useState(false);
  
  const audioRef = useRef(null);
  const recordingIntervalRef = useRef(null);
  const holdTimeoutRef = useRef(null);

  useEffect(() => {
    return () => {
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
      }
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
      if (audioUrl) {
        URL.revokeObjectURL(audioUrl);
      }
    };
  }, [audioUrl]);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const recorder = new MediaRecorder(stream, {
        mimeType: 'audio/webm;codecs=opus'
      });
      
      const chunks = [];
      
      recorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          chunks.push(event.data);
        }
      };
      
      recorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/webm' });
        setAudioBlob(blob);
        const url = URL.createObjectURL(blob);
        setAudioUrl(url);
        
        // Stop all tracks
        stream.getTracks().forEach(track => track.stop());
      };
      
      recorder.start();
      setMediaRecorder(recorder);
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer with 100ms precision for smooth counting
      recordingIntervalRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 0.1);
      }, 100);
      
    } catch (error) {
      console.error('Error starting recording:', error);
      alert('Unable to access microphone. Please check permissions.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorder && isRecording) {
      mediaRecorder.stop();
      setIsRecording(false);
      
      if (recordingIntervalRef.current) {
        clearInterval(recordingIntervalRef.current);
        recordingIntervalRef.current = null;
      }
    }
  };

  const handleMouseDown = () => {
    setIsHolding(true);
    holdTimeoutRef.current = setTimeout(() => {
      startRecording();
    }, 200); // Small delay to prevent accidental recording
  };

  const handleMouseUp = () => {
    setIsHolding(false);
    if (holdTimeoutRef.current) {
      clearTimeout(holdTimeoutRef.current);
    }
    if (isRecording) {
      stopRecording();
    }
  };

  const handleMouseLeave = () => {
    if (isHolding) {
      setIsHolding(false);
      if (holdTimeoutRef.current) {
        clearTimeout(holdTimeoutRef.current);
      }
      if (isRecording) {
        stopRecording();
      }
    }
  };

  const playRecording = () => {
    if (audioRef.current) {
      audioRef.current.playbackRate = playbackSpeed;
      audioRef.current.play();
      setIsPlaying(true);
    }
  };

  const pauseRecording = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleAudioTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleAudioLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
    }
  };

  const handleSpeedChange = (speed) => {
    setPlaybackSpeed(speed);
    if (audioRef.current) {
      audioRef.current.playbackRate = speed;
    }
  };

  const handleRestart = () => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      setCurrentTime(0);
    }
  };

  const handleSend = () => {
    if (audioBlob) {
      onRecordingComplete(audioBlob);
    }
  };

  const handleCancel = () => {
    if (audioUrl) {
      URL.revokeObjectURL(audioUrl);
    }
    setAudioBlob(null);
    setAudioUrl(null);
    setRecordingTime(0);
    setIsPlaying(false);
    onCancel();
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatAudioTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  // WhatsApp-style waveform animation
  const getWaveformBars = () => {
    const bars = 12;
    const data = [];
    for (let i = 0; i < bars; i++) {
      if (isRecording) {
        // Animated bars during recording
        data.push(Math.random() * 0.9 + 0.1);
      } else if (audioBlob) {
        // Static bars for recorded audio
        data.push(0.3 + Math.sin(i * 0.5) * 0.4);
      } else {
        // Empty bars
        data.push(0.1);
      }
    }
    return data;
  };

  return (
    <div className="bg-white rounded-lg shadow-lg p-4 max-w-sm mx-auto">
      {/* WhatsApp-style Recording Interface */}
      {!audioBlob ? (
        <div className="text-center">
          {/* Recording Button */}
          <div className="mb-4">
            <button
              onMouseDown={handleMouseDown}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
              onTouchStart={handleMouseDown}
              onTouchEnd={handleMouseUp}
              className={`p-6 rounded-full transition-all duration-200 ${
                isHolding || isRecording
                  ? 'bg-red-500 text-white scale-110 shadow-lg'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
              disabled={isRecording}
            >
              {isRecording ? (
                <MicOff className="w-8 h-8 animate-pulse" />
              ) : (
                <Mic className="w-8 h-8" />
              )}
            </button>
          </div>

          {/* WhatsApp-style Recording Box */}
          {isRecording && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center justify-center space-x-3">
                {/* Recording Indicator */}
                <div className="flex items-center space-x-2">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                  <span className="text-red-600 font-medium">Recording</span>
                </div>
                
                {/* Timer */}
                <div className="text-red-600 font-mono text-lg font-bold">
                  {formatTime(recordingTime)}
                </div>
              </div>
              
              {/* Waveform Visualization */}
              <div className="flex items-end justify-center space-x-1 h-8 mt-3">
                {getWaveformBars().map((height, index) => (
                  <div
                    key={index}
                    className="bg-red-500 rounded-sm animate-pulse"
                    style={{
                      width: '2px',
                      height: `${height * 100}%`,
                      animationDelay: `${index * 0.1}s`
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Status Messages */}
          <div className="mb-4">
            {!isRecording && isHolding && (
              <div className="text-gray-600">
                <span className="font-medium">Hold to record</span>
              </div>
            )}
            {!isRecording && !isHolding && (
              <div className="text-gray-600">
                <span className="font-medium">Hold and speak</span>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="text-sm text-gray-500">
            {isRecording ? (
              <p>Release to stop recording</p>
            ) : (
              <p>Hold the microphone button to record</p>
            )}
          </div>
        </div>
      ) : (
        /* WhatsApp-style Playback Interface */
        <div className="space-y-4">
          {/* Audio Player */}
          <div className="bg-gray-50 rounded-lg p-4">
            {/* Progress Bar */}
            <div className="flex items-center space-x-3 mb-3">
              <button
                onClick={isPlaying ? pauseRecording : playRecording}
                className="p-2 bg-blue-500 text-white rounded-full hover:bg-blue-600 transition-colors"
              >
                {isPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              </button>
              
              <div className="flex-1">
                <div className="w-full h-2 bg-gray-300 rounded-full">
                  <div
                    className="h-full bg-blue-500 rounded-full transition-all"
                    style={{ width: `${duration > 0 ? (currentTime / duration) * 100 : 0}%` }}
                  />
                </div>
              </div>
              
              <span className="text-sm text-gray-600 min-w-[40px]">
                {formatAudioTime(currentTime)} / {formatAudioTime(duration)}
              </span>
            </div>

            {/* Speed Controls */}
            <div className="flex items-center justify-center space-x-1">
              <Volume2 className="w-4 h-4 text-gray-500" />
              {[0.5, 1, 1.5, 2].map((speed) => (
                <button
                  key={speed}
                  onClick={() => handleSpeedChange(speed)}
                  className={`px-2 py-1 text-xs rounded ${
                    playbackSpeed === speed
                      ? 'bg-blue-500 text-white'
                      : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                  } transition-colors`}
                >
                  {speed}x
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-center space-x-3">
            <button
              onClick={handleRestart}
              className="p-3 bg-gray-400 text-white rounded-full hover:bg-gray-500 transition-colors"
              title="Restart"
            >
              <RotateCcw className="w-4 h-4" />
            </button>

            <button
              onClick={handleSend}
              className="p-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
              title="Send Voice Message"
            >
              <Send className="w-4 h-4" />
            </button>

            <button
              onClick={handleCancel}
              className="p-3 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
              title="Delete Recording"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Recording Info */}
          <div className="text-center text-sm text-gray-500">
            <p className="font-medium text-green-600">✅ Voice message ready</p>
            <p>Duration: {formatTime(recordingTime)}</p>
          </div>
        </div>
      )}

      {/* Hidden Audio Element */}
      {audioUrl && (
        <audio
          ref={audioRef}
          src={audioUrl}
          onTimeUpdate={handleAudioTimeUpdate}
          onLoadedMetadata={handleAudioLoadedMetadata}
          onEnded={handleAudioEnded}
          className="hidden"
        />
      )}
    </div>
  );
};

export default VoiceRecorder;
